import { FC, useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useNavigate } from "react-router-dom";
import HubInputField from "../../components/inputType";
import Footer from "src/components/footer";
import { AuthContext } from "../../context/authContext";
import { ToastContext } from "../../context/toastContext";
import { LoginSchema } from "../../validation/login";
import useFetch from "../../hooks/useFetch";
import { APIEndpoints } from "../../types/apiTypes";
import { LoginFormType, LoginAPIResponseType } from "./types";
import { ToastTypes } from "../../types";
import { UserType } from "src/types/users";
import { USER_TYPES } from "src/constants";
import { paciraLogo } from "src/assets";
import { PHONE_NUMBER } from "src/constants/contact";

const HubLogin: FC = (): JSX.Element => {
  const { control, handleSubmit } = useForm({
    mode: 'all',
    resolver: yupResolver(LoginSchema),
  });
  const [isPasswordVisible, setPasswordVisibility] = useState<boolean>(false);
  const { userData: sessionSavedUser, createSession, restoreSession } = useContext(AuthContext);
  const { showToast } = useContext(ToastContext);
  const navigate = useNavigate();
  const singleSignOn = localStorage.getItem("fromSingleSignOn")


  const reRouteRelevantUser = (userData: UserType) => {
    if (singleSignOn) {
      if (USER_TYPES.MANUFACTURER.includes(userData.ref_type)) {
        if (userData.ref_type === "rep") {
          navigate(`/territories/${userData.id}`);
        } else {
          if (userData && userData.ref_type === "regional_manager") {
            navigate(`/regions/${userData.id}`);
          } else if (userData && userData.ref_type === "district_manager") {
            navigate(`/districts/${userData.id}`);
          } else {
            navigate("/statistics");
          }
        }
      } else if (USER_TYPES.HCP.includes(userData.ref_type)) {
        navigate("/cases");
      }
    } else {
      if (!userData.is_password_changed) {
        navigate("/users/createuser");
      } else {

        if (USER_TYPES.MANUFACTURER.includes(userData.ref_type)) {

          if (userData.ref_type === "rep") {

            navigate(`/territories/${userData.id}`);
          } else {
            if (userData && userData.ref_type === "regional_manager") {

              navigate(`/regions/${userData.id}`);
            } else if (userData && userData.ref_type === "district_manager") {

              navigate(`/districts/${userData.id}`);

            } else {
              navigate("/statistics");
            }
          }
        } else if (USER_TYPES.HCP.includes(userData.ref_type)) {
          navigate("/cases");
        }
      }
    }
  };

  const { data: userData, loading, mutate, error, removeErrorAndData } = useFetch<LoginAPIResponseType>(APIEndpoints.USER_SIGNIN);

  useEffect(() => {
    if (userData && userData.status.code === 200) {
      const { status, token } = userData;
      createSession(
        {
          ...status.data.user,
          practice_created_date: status.data.address ? status.data.address.date : "",
          practice_name: status.data.address ? status.data.address.name : "",
          address: status.data.address ? status.data.address.ref_address : "",
          reset_demo_cases: status.data.navbar.reset_demo_cases,
          stats_view: status.data.navbar.stats_view,
          demo_user_view: status.data.navbar.demo_user_view,
          eob_button: status.data.navbar.eob_button || false,
        },
        status.data.sub_header,
        token,
      );
      showToast([status.message], ToastTypes.INFO);

      reRouteRelevantUser(status.data.user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  const submitForm = async (values: LoginFormType) => {
    if ((error && error.length > 0) || userData) removeErrorAndData();
    const isSessionExists = restoreSession();

    if (!isSessionExists) {
      mutate({ user: values });
    } else {
      showToast([`There is already an active user session`], ToastTypes.INFO);
      if (sessionSavedUser) reRouteRelevantUser(sessionSavedUser);
    }
  };

  return (
    <div className="container-fluid login-sec d-flex flex-fill flex-column justify-content-between overflow-auto">
      <div className="container d-flex flex-fill flex-column align-items-center pt-4 pb-4 ps-4 pe-4">
        <div className="row justify-content-center align-items-center flex-fill mb-3">
          <div className="col-12 col-lg-6 col-md-5">
            <h1 className="login-text">Keeping more of their recovery in your hands</h1>
          </div>
          <div className="col-12 col-lg-2 col-md-1"></div>
          <div className="col-12 col-lg-4 col-md-6">
            <form className="card shadow" onSubmit={handleSubmit(submitForm)}>
              <div className="card-header">
                <h3 className="m-0">Sign in</h3>
                <small className="m-0">Please sign in with your credentials</small>
              </div>

              <div className="card-body">
                <div className="mb-4">
                  <Controller
                    name="email"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <HubInputField
                        inputId="email"
                        inputPlaceholder="Email"
                        isRowBased={false}
                        isRequired={true}
                        field={field}
                        error={error}
                        inputType="email"
                      />
                    )}
                  />
                </div>
                <Controller
                  name="password"
                  control={control}
                  defaultValue=""
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <div className="input-group mb-3">
                        <input
                          type={isPasswordVisible ? "text" : "password"} placeholder="Password"
                          className="form-control" {...field} />
                        <button type="button" className="btn btn-secondary"
                          onClick={() => setPasswordVisibility((isPasswordVisible) => !isPasswordVisible)}
                        >
                          {isPasswordVisible ? (
                            <i className="bi bi-eye-fill"></i>
                          ) : (
                            <i className="bi bi-eye-slash"></i>
                          )}
                        </button>
                      </div>
                      {error && (<p className="mb-0 text-danger">{error.message}</p>)}
                    </>
                  )}
                />
                <p className="small m-0">By clicking sign in below, you agree to our&nbsp;
                  <a href="https://www.exparelpro.com/terms-of-use" target="_blank" rel="noreferrer">
                    Terms of Use
                  </a>
                </p>
              </div>

              <div className="card-footer d-flex align-items-center justify-content-between">
                <button disabled={loading} className="btn btn-primary" type="submit">
                  {loading ? (<i className="spinner-border"></i>) : null} Sign In
                </button>
                <Link to="/users/forgotpassword" className="text-black text-decoration-none">
                  Forgot your password?
                </Link>
              </div>
            </form>
          </div>
        </div>
        <div>
          <p className="mb-3 text-white" style={{ fontSize: '1rem' }}>
            For more information, please visit&nbsp;
            <a target="_blank" className="text-secondary" href="https://www.exparelpro.com"
              rel="noreferrer">www.exparelpro.com</a>&nbsp;
            or call {PHONE_NUMBER}. You are encouraged to report negative side effects of prescription drugs to
            FDA; visit&nbsp;
            <a target="_blank" className="text-secondary" href="http://www.fda.gov/medwatch" rel="noreferrer">
              MedWatch: FDA Safety Information & Adverse Event Reporting Program
            </a>&nbsp;
            or call 1-800-FDA-1088.
          </p>
          <div className="row align-items-center">
            <div className="col-12 col-sm-2">
              <img className="img-fluid" width={170} src={paciraLogo} alt="Products Logos" />
            </div>
            <p className="col text-white m-0" style={{ fontSize: '1rem' }}>
              © 2026 Pacira Pharmaceuticals, Inc., a wholly owned subsidiary of Pacira BioSciences, Inc. EXPAREL is a
              registered trademark of Pacira Therapeutics, Inc. PP-EX-US-9728 May 2026
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default HubLogin;
